import { useState } from "react"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import { pluginsApi } from "@/api/plugins-api"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

import { pluginErrorCode, pluginErrorMessage, splitIdentifiers } from "./plugin-utils"
import { usePluginQueryKey } from "./use-plugin-query-key"

export function PluginMigration() {
  const { t } = useTranslation()
  const pluginKey = usePluginQueryKey()
  const client = useQueryClient()
  const [agent, setAgent] = useState("explorer")
  const [label, setLabel] = useState("")
  const [operations, setOperations] = useState("")
  const [confirmed, setConfirmed] = useState(false)
  const [transport, setTransport] = useState<"streamable_http" | "sse">("streamable_http")
  const [privateNetworks, setPrivateNetworks] = useState("")
  const options = { transport, network: { private_cidrs: splitIdentifiers(privateNetworks) } }
  const preview = useMutation({
    mutationFn: () => pluginsApi.migrationPreview(agent, label, options),
    onSuccess: (data) => {
      setOperations(data.allowed_tools?.join(", ") ?? "")
      setConfirmed(false)
    },
  })
  const migrate = useMutation({
    mutationFn: () => pluginsApi.migrate(preview.data!, splitIdentifiers(operations), confirmed),
    onSuccess: () => client.invalidateQueries({ queryKey: pluginKey() }),
  })
  const current =
    preview.data?.agent === agent &&
    preview.data.label === label &&
    (preview.data.resume ||
      (preview.data.transport === transport &&
        JSON.stringify(preview.data.network) === JSON.stringify(options.network)))
      ? preview.data
      : null
  const error = preview.error ?? migrate.error
  return (
    <details className="rounded-lg border p-3">
      <summary className="cursor-pointer font-medium">{t("plugins.migrationTitle")}</summary>
      <div className="mt-3 space-y-3">
        <p className="text-muted-foreground text-sm">{t("plugins.migrationDescription")}</p>
        <Label>{t("plugins.agent")}</Label>
        <Select value={agent} onValueChange={setAgent}>
          <SelectTrigger aria-label={t("plugins.agent")}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="explorer">Explorer / Deep</SelectItem>
            <SelectItem value="dash">Dash</SelectItem>
          </SelectContent>
        </Select>
        <Label htmlFor="legacy-mcp-label">{t("plugins.legacyLabel")}</Label>
        <Input
          id="legacy-mcp-label"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
        />
        <Label>{t("plugins.transport")}</Label>
        <Select value={transport} onValueChange={(value: typeof transport) => setTransport(value)}>
          <SelectTrigger aria-label={t("plugins.transport")}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="streamable_http">Streamable HTTP</SelectItem>
            <SelectItem value="sse">SSE</SelectItem>
          </SelectContent>
        </Select>
        <Label htmlFor="migration-networks">{t("plugins.privateNetworks")}</Label>
        <Input
          id="migration-networks"
          value={privateNetworks}
          onChange={(event) => setPrivateNetworks(event.target.value)}
        />
        <Button
          disabled={!label || preview.isPending}
          variant="outline"
          onClick={() => preview.mutate()}
        >
          {t("plugins.migrationPreview")}
        </Button>
        {current && (
          <div className="space-y-3">
            <p className="text-sm">{current.base_url}</p>
            {current.audience && (
              <div className="text-muted-foreground text-sm">
                {current.audience.everyone
                  ? t("plugins.everyone")
                  : t("plugins.migrationAudience", {
                      users: current.audience.users.length,
                      groups: current.audience.groups.length,
                    })}
              </div>
            )}
            {current.blockers.map((reason) => (
              <p key={reason} className="text-warning-foreground text-sm" role="alert">
                {pluginErrorMessage(t, reason, { fallback: t("plugins.migrationBlocked") })}
              </p>
            ))}
            {!current.resume && (
              <>
                <Label htmlFor="legacy-mcp-operations">{t("plugins.approveOperations")}</Label>
                <Input
                  id="legacy-mcp-operations"
                  value={operations}
                  onChange={(event) => setOperations(event.target.value)}
                />
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={confirmed}
                    onCheckedChange={(checked) => setConfirmed(checked === true)}
                  />
                  {t("plugins.confirmReadOnly")}
                </label>
              </>
            )}
            <Button
              disabled={
                migrate.isPending ||
                Boolean(current.blockers.length) ||
                (!current.resume && (!confirmed || !splitIdentifiers(operations).length))
              }
              onClick={() => migrate.mutate()}
            >
              {current.resume ? t("plugins.resumeMigration") : t("plugins.migrate")}
            </Button>
            {migrate.isSuccess && <p role="status">{t("plugins.migrationComplete")}</p>}
          </div>
        )}
        {error && (
          <p className="text-destructive text-sm" role="alert">
            {pluginErrorMessage(t, pluginErrorCode(error))}
          </p>
        )}
      </div>
    </details>
  )
}
